import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentCardWithInput,
  InputText,
  InputTextarea,
  Switch,
} from '@teo/components';
import {
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  Import,
  Plus,
} from '@teo/components/icons';
import { useGet } from '@/query/fetchHooks';
import { nthLastIndexOf, parseCourseType, isEmpty, round2 } from '@/util';
import ActionLP from './components/ActionLP';
import SettingsLP from './components/SettingsLP';
import ImportLP from './components/ImportLP';
import { axios } from '@/axios';
import { config } from '@/config';
import { useMediaQuery } from 'react-responsive';
import CompetenciesList from './components/CompetenciesList';
import CompetenceConditions, {
  useConditionCompetencies,
  conditionCompetenceIds,
} from '@/components/LearningPath/CompetenceConditions';

const backgroundColor = '#EAEAED';
const blurColor = '#C2CDFE';
const focusColor = '#7089FD';

const CourseSettings = ({
  learningpathId,
  nodeSelected,
  setIsLoaderOpen,
  handleChangeNode,
  handleRemoveNode,
  handleMakeTheme,
  handleRemoveChildNode,
  setNodes,
  nodes,
  setEdges,
  edges,
}) => {
  const { t } = useTranslation();
  const [courseDetail, setCourseDetail] = useState(null);
  const [inOrderCourse, setInOrderCourse] = useState(false);
  const [correctionType, setCorrectionType] = useState('manual');
  const [minScore, setMinScore] = useState(0);
  const percentageMinScore = round2(minScore ? minScore * 100 : 0.5 * 100);
  const [trackColor, setTrackColor] = useState(blurColor);
  const examplePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  const [titleImport, setTitleImport] = useState(nodeSelected?.data?.title);
  const [descriptionImport, setDescriptionImport] = useState(
    nodeSelected?.data?.description || ''
  );
  const [courseType, setCourseType] = useState('course');

  const [isOpenCompetenciesModal, setIsOpenCompetenciesModal] = useState();
  // const [competencies, setCompetencies] = useState(null);

  const [courseCompetencies, setCourseCompetencies] = useState(null);

  const [competenciesConditions, setCompetenciesConditions] = useState(false);
  const [competenciesConditionsArr, setCompetenciesConditionsArr] =
    useState(null);
  const [conditionIds, setConditionIds] = useState([]);

  // A node can belong to a nested learning path (see handleRemoveChildNode)
  const nodeLearningpathId =
    nodeSelected?.data?.learningPathId || learningpathId;

  useGet(`/learningpathnode/${nodeLearningpathId}/${nodeSelected?.id}`, {
    retry: false,
    enabled:
      !!nodeLearningpathId &&
      !!nodeSelected?.id &&
      nodeSelected?.data?.type === 'course',
    onSuccess: (result) => {
      setCompetenciesConditions(!!result?.data?.competenciesConditions);
      setConditionIds(result?.data?.competenciesConditionsArr || []);
      if (!result?.data?.competenciesConditionsArr?.length) {
        setCompetenciesConditionsArr([]);
      }
    },
  });

  useConditionCompetencies(conditionIds, setCompetenciesConditionsArr);

  const saveConditions = (change) => {
    handleChangeNode?.(nodeSelected?.id, change, nodeLearningpathId);
  };

  const handleSaveConditionList = (list) => {
    const ids = conditionCompetenceIds(list);
    setCompetenciesConditionsArr(list || []);
    setConditionIds(ids);
    saveConditions({ competenciesConditionsArr: ids });
  };

  useGet(`/coursedetail/${nodeSelected?.data?.courseId}`, {
    retry: false,
    enabled: !!nodeSelected?.data?.courseId,
    onSuccess: (result) => {
      setCourseDetail(result?.data || {});
      setCourseType(
        result?.data?.publicationCourseType
          ? result?.data?.publicationCourseType
          : 'course'
      );
      setTitleImport(result?.data?.courseName);
      setDescriptionImport(result?.data?.courseDescription);
      setInOrderCourse(result?.data?.inOrder);
      setCorrectionType(result?.data?.correctionType);
      setMinScore(result?.data?.minScore);
      setNodes((nds) =>
        nds?.map((node) =>
          node.id === nodeSelected.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  coursecontentId: result?.data?.coursecontentId,
                  publicationCourseType: result?.data?.publicationCourseType,
                },
              }
            : node
        )
      );
    },
  });

  useGet(`/coursecontenteffectivecompetences`, {
    coursecontentId: courseDetail?.coursecontentId,
    sourceType:
      'coursecontent,OR,sourceType=directory,OR,sourceType=lesson,OR,sourceType=widget',
    enabled: !!courseDetail?.coursecontentId,
    onSuccess: (result) => {
      setCourseCompetencies(result?.data);
    },
  });

  const save = async () => {
    const changesCourse = {};
    if (titleImport !== courseDetail?.courseName) {
      changesCourse.courseName = titleImport;
    }
    if (descriptionImport !== courseDetail?.courseDescription) {
      changesCourse.courseDescription = descriptionImport;
    }
    const promises = [];
    if (!isEmpty(changesCourse)) {
      promises.push(
        axios.put(`${config.backend}/course/${courseDetail?.id}`, changesCourse)
      );
    }
  };

  const habdleChangeTitle = (value) => {
    setTitleImport(value);
    setNodes(
      nodes?.map((node) =>
        node.id === nodeSelected.id
          ? { ...node, data: { ...node.data, title: value } }
          : node
      )
    );
  };

  const habdleChangeDescription = (value) => {
    setDescriptionImport(value);
    setNodes(
      nodes?.map((node) =>
        node.id === nodeSelected.id
          ? { ...node, data: { ...node.data, description: value } }
          : node
      )
    );
  };

  const handleChangeCourse = async (change) => {
    if (courseDetail) {
      const result = await axios.put(
        `${config.backend}/course/${courseDetail?.id}`,
        { coursecontentId: courseDetail?.coursecontentId, ...change }
      );
      setInOrderCourse((prev) => result?.data?.inOrder || prev);
      setCorrectionType((prev) => result?.data?.correctionType || prev);
      setMinScore((prev) => result?.data?.minScore || prev);
    }
  };

  return (
    <div
      className={`flex h-full max-h-[calc(100vh_-_150px)] flex-col overflow-hidden ${
        isMobile && 'min-h-[calc(100vh_-_126px)]'
      }`}
    >
      <div className="flex h-full max-h-[calc(100vh_-_200px)] flex-col overflow-y-auto">
        {nodeSelected?.data?.courseType !== 'import' ? (
          <ContentCardWithInput
            description={courseDetail?.description}
            icon={
              nodeSelected?.data?.courseType === 'course'
                ? DuoToneCourseFilled
                : nodeSelected?.data?.courseType === 'assessment'
                ? DuoToneAssessmentFilled
                : DuoToneVacancyFilled
            }
            title={nodeSelected?.data?.title}
            color={
              nodeSelected?.data?.courseType === 'course'
                ? 'yellow'
                : nodeSelected?.data?.courseType === 'assessment'
                ? 'red'
                : 'yellow'
            }
            classNameIcon={
              nodeSelected?.data?.courseType === 'course'
                ? 'fill-primary-06 text-primary-01'
                : nodeSelected?.data?.courseType === 'assessment'
                ? 'fill-tint-red-dark text-tint-red-light'
                : 'text-error-04 fill-error-04'
            }
          />
        ) : (
          <ContentCardWithInput icon={Import} color={'light_blue'}>
            <div className="pt-4">
              <InputText
                value={titleImport}
                onChange={(e) => habdleChangeTitle(e.currentTarget.value)}
                className="mb-2"
                onBlur={save}
              />
              <InputTextarea
                placeholder={t('pages.learning_path.imported_description')}
                onChange={(event) =>
                  habdleChangeDescription(event.target.value)
                }
                value={descriptionImport}
                // limit={300}
                minRows={1}
                customStyles="bg-white inputWhiteBg  text-sm px-[6px]"
                onBlur={save}
              />
            </div>
          </ContentCardWithInput>
        )}
        {nodeSelected?.data?.courseType === 'import' && (
          <ImportLP
            nodeSelected={nodeSelected}
            courseDetail={courseDetail}
            setCourseDetail={setCourseDetail}
            setCourseType={setCourseType}
            courseType={courseType}
            setIsLoaderOpen={setIsLoaderOpen}
            name={titleImport}
            description={descriptionImport}
            setNodes={setNodes}
          />
        )}
        {nodeSelected?.data?.learningPathId == learningpathId && (
          <ActionLP
            nodeSelected={nodeSelected}
            edges={edges}
            nodes={nodes}
            setNodes={setNodes}
            handleMakeTheme={handleMakeTheme}
          />
        )}
        <SettingsLP
          learningpathId={learningpathId}
          nodeSelected={nodeSelected}
          courseDetail={courseDetail}
          handleChangeNode={handleChangeNode}
          edges={edges}
          setEdges={setEdges}
          nodes={nodes}
          setNodes={setNodes}
        />
        {nodeSelected?.data?.type === 'course' && (
          <div className="px-5">
            <div className=" flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
              <div data-testid="inOrder">
                <Switch
                  checked={inOrderCourse}
                  onChange={(isCheck) => {
                    setInOrderCourse(isCheck);
                    handleChangeCourse({ inOrder: isCheck });
                  }}
                />
              </div>
              <div>
                <h4 className="pb-2 font-normal">
                  {t('pages.course_content_flow.step_3.tracking')}
                </h4>
                <p className="text-xs text-grey-05">
                  {t('pages.course_content_flow.step_3.tracking_description')}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
              <div data-testid="autoCorrect">
                <Switch
                  checked={
                    correctionType === 'immediate' ||
                    correctionType === 'autofeedback'
                  }
                  onChange={(isCheck) => {
                    setCorrectionType(isCheck ? 'immediate' : 'manual');
                    handleChangeCourse({
                      correctionType: isCheck ? 'immediate' : 'manual',
                    });
                  }}
                />
              </div>
              <div>
                <h4 className="pb-2 font-normal">
                  {t('pages.course_content_flow.step_3.correction')}
                </h4>
                <p className="text-xs text-grey-05">
                  {t('pages.course_content_flow.step_3.correction_description')}
                </p>
              </div>
            </div>
            {(correctionType === 'immediate' ||
              correctionType === 'autofeedback') && (
              <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
                <div data-testid="autoCorrect2">
                  <Switch
                    checked={correctionType === 'autofeedback'}
                    onChange={(isCheck) => {
                      setCorrectionType(isCheck ? 'autofeedback' : 'immediate');
                      handleChangeCourse({
                        correctionType: isCheck ? 'autofeedback' : 'immediate',
                      });
                    }}
                  />
                </div>
                <div>
                  <h4 className="pb-2 font-normal">
                    {t(
                      'pages.course_content_flow.step_3.correction_autofeedback'
                    )}
                  </h4>
                  <p className="text-xs text-grey-05">
                    {t(
                      'pages.course_content_flow.step_3.correction_autofeedback_description'
                    )}
                  </p>
                </div>
              </div>
            )}
            <div className="my-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 px-4 pt-6 pb-4">
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full">
                  <input
                    className="slider w-full flex-shrink"
                    max={100}
                    min={0}
                    onChange={(e) => {
                      setMinScore(parseFloat(e?.currentTarget.value) / 100);
                      handleChangeCourse({
                        minScore: parseFloat(e?.currentTarget.value) / 100,
                      });
                    }}
                    onMouseDown={() => setTrackColor(focusColor)}
                    onMouseUp={() => setTrackColor(blurColor)}
                    onTouchEnd={() => setTrackColor(blurColor)}
                    onTouchStart={() => setTrackColor(focusColor)}
                    step={5}
                    style={{
                      background: `linear-gradient(to right, ${trackColor} 0%, ${trackColor} ${percentageMinScore}%, ${backgroundColor} ${percentageMinScore}%, ${backgroundColor})`,
                    }}
                    value={percentageMinScore}
                    type="range"
                  />
                </div>
                <div className="flex flex-row items-center gap-3">
                  <p className="min-w-max text-sm">
                    {t('pages.course_content_flow.step_3.minimum_score')}
                  </p>
                  <InputText
                    type="number"
                    value={percentageMinScore}
                    onChange={(e) =>
                      setMinScore(parseFloat(e?.currentTarget.value) / 100)
                    }
                    suffix="%"
                    className="w-[90px]"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {nodeSelected?.data?.type === 'course' && (
          <div className=" mb-6 border-t px-5 pt-5">
            <h4 className="mb-4 text-base">
              {t('pages.learning_path.conditions.conditions')}
            </h4>
            <CompetenceConditions
              className="mt-2"
              enabled={competenciesConditions}
              onToggle={(isCheck) => {
                setCompetenciesConditions(isCheck);
                saveConditions({ competenciesConditions: isCheck });
              }}
              competencies={competenciesConditionsArr || []}
              onChange={handleSaveConditionList}
            />
          </div>
        )}
        {courseCompetencies?.length > 0 && (
          <div className=" mb-6 border-t px-5 pt-5">
            <h4 className="mb-4 text-base">{t('links.competencies')}</h4>

            <Button
              variant="outline"
              // className="flex w-full items-center gap-2 border !border-grey-02 !p-4 !text-[#1E1E2080]"
              onClick={() => {
                setIsOpenCompetenciesModal(true);
              }}
            >
              {t('pages.learning_path.show_competencies')}
            </Button>
            {isOpenCompetenciesModal && (
              <CompetenciesList
                setIsOpenCompetencies={setIsOpenCompetenciesModal}
                isOpenCompetencies={isOpenCompetenciesModal}
                courseCompetencies={courseCompetencies}
                coursecontentId={courseDetail?.coursecontentId}
              />
            )}
          </div>
        )}
      </div>
      <div className="mt-auto flex flex-col gap-2 border-t px-5 py-5 shadow-[0px_7px_30px_-9px_grey]">
        <Button
          variant="outline"
          className=""
          onClick={() => {
            window.open(
              nodeSelected?.data?.courseType !== 'import'
                ? `${examplePath}/${parseCourseType(
                    courseDetail?.publicationCourseType
                  )}/${
                    courseDetail?.coursecontentId
                  }/preview?les=0&new_tab=true`
                : `${examplePath}/${parseCourseType(
                    courseDetail?.publicationCourseType
                  )}/${courseDetail?.coursecontentId}`,
              '_blank'
            );
          }}
        >
          {nodeSelected?.data?.courseType === 'course' &&
            t('pages.learning_path.view_cr')}
          {nodeSelected?.data?.courseType === 'assessment' &&
            t('pages.learning_path.view_as')}
          {nodeSelected?.data?.courseType === 'leervacature' &&
            t('pages.learning_path.view_vc')}
          {nodeSelected?.data?.courseType === 'import' &&
            t('pages.learning_path.view_im')}
          {}
        </Button>
        <Button
          variant="ghost"
          className="!text-error-04"
          onClick={() => {
            nodeSelected?.data?.learningPathId == learningpathId
              ? handleRemoveNode(nodeSelected)
              : handleRemoveChildNode(nodeSelected, nodes);
          }}
        >
          {nodeSelected?.data?.courseType === 'course' &&
            t('pages.learning_path.delete_cr')}
          {nodeSelected?.data?.courseType === 'assessment' &&
            t('pages.learning_path.delete_as')}
          {nodeSelected?.data?.courseType === 'leervacature' &&
            t('pages.learning_path.delete_vc')}
          {nodeSelected?.data?.courseType === 'import' &&
            t('pages.learning_path.delete_im')}
        </Button>
      </div>
    </div>
  );
};

export default CourseSettings;
